import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CInputGroup,
  CInputCheckbox,
  CSelect,
} from "@coreui/react";
import React, { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  createItem,
  updateItem,
  ItemRequestStatus,
  getItem,
} from "../../api/generics";
import Errors, { getFieldErrors } from "../../models/errors";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert } from "../utils/messages";
import { emptyValueOnUndefined } from "../../utils/fields";
import Loading from "../indicators/Loading";
import {
  PERMISSION_GROUPS,
  ROLE_PERMISSIONS,
  ROLES,
} from "../../auth/permissions";
import CompanyUser, { newCompanyUser } from "../../models/company-user";
import { useSelector } from "react-redux";

interface UserFormProps {
  initialUser?: CompanyUser;
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const UserForm: React.FC<UserFormProps> = ({
  initialUser,
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState<CompanyUser>(
    initialUser ? initialUser : newCompanyUser()
  );

  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {}
  );
  const [submitting, setSubmitting] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  const companyId = useSelector((state: any) => state.company.data.company.id);

  const onFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingUser({
      ...editingUser,
      firstName: e.target.value,
    });
  };

  const onLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingUser({
      ...editingUser,
      lastName: e.target.value,
    });
  };

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingUser({
      ...editingUser,
      email: e.target.value,
    });
  };

  const onPassword1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingUser({
      ...editingUser,
      password1: e.target.value,
    });
  };

  const onPassword2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingUser({
      ...editingUser,
      password2: e.target.value,
    });
  };

  const onPassword1ShowClick = () => {
    setShowPassword1(!showPassword1);
  };

  const onPassword2ShowClick = () => {
    setShowPassword2(!showPassword2);
  };

  const onRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRole = e.target.value;

    // Get default permissions for this role from ROLE_PERMISSIONS
    const defaultPermissions = newRole ? ROLE_PERMISSIONS[newRole] || [] : [];

    setEditingUser({
      ...editingUser,
      role: newRole,
      // Assign the default permissions for the selected role
      permissions: [...defaultPermissions],
    });
  };

  const onPermissionsChange = useCallback(
    (permissionName: string, enabled: boolean) => {
      const newEditingUser = { ...editingUser };
      if (newEditingUser.permissions === undefined) {
        newEditingUser.permissions = [];
      }

      const permissionIndex =
        newEditingUser.permissions.indexOf(permissionName);

      if (enabled && permissionIndex < 0) {
        newEditingUser.permissions.push(permissionName);
      }

      if (!enabled && permissionIndex >= 0) {
        const permissionIndex =
          newEditingUser.permissions.indexOf(permissionName);
        newEditingUser.permissions.splice(permissionIndex, 1);
      }

      setEditingUser(newEditingUser);
    },
    [editingUser]
  );

  const onSave = useCallback(async () => {
    setSubmitting(true);
    let toSendUser = {
      ...editingUser,
      companyId: companyId,
    };
    let requestPromise: Promise<ItemRequestStatus<CompanyUser>>;
    if (editingUser.id === undefined) {
      requestPromise = createItem<CompanyUser>(
        `/company_users/${companyId}/users/create/`,
        toSendUser
      );
    } else {
      requestPromise = updateItem<CompanyUser>(
        `/company_users/${companyId}/users/${toSendUser.id}/`,
        toSendUser
      );
    }

    const userStatus = await requestPromise;

    if (userStatus.status !== SUCCESS) {
      if (userStatus.errors !== undefined) {
        setErrors(userStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (userStatus.detail !== undefined) {
        message = userStatus.detail;
      }
      errorAlert(message);
    } else {
      setErrors({});
      clearForm();
      onSuccess();
    }
    setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, editingUser]);

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setEditingUser(newCompanyUser());
  };

  const loadUser = useCallback(async () => {
    if (initialUser === undefined || initialUser.id === undefined) {
      setEditingUser(newCompanyUser());
      return;
    }

    setLoading(true);

    const requestStatus = await getItem<CompanyUser>(
      `/company_users/${companyId}/users/${initialUser.id}/`
    );

    if (requestStatus.status !== SUCCESS || requestStatus.data === undefined) {
      setEditingUser(initialUser);
      setLoading(false);
      return;
    }

    setEditingUser(requestStatus.data);

    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, initialUser]);

  useEffect(() => {
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUser]);

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialErrors]);

  if (loading) {
    return <Loading height="30vh" />;
  }

  return (
    <>
      <fieldset disabled={submitting}>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Nombre</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingUser.firstName)}
                onChange={onFirstNameChange}
                placeholder="Ej: Nombre"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("firstName", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Apellido</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingUser.lastName)}
                onChange={onLastNameChange}
                placeholder="Ej: Apellido"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("lastName", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Correo electrónico</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingUser.email)}
                onChange={onEmailChange}
                placeholder="Ej: Correo electrónico"
                autoComplete="new-email"
                readOnly={initialUser?.id !== undefined}
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("email", errors) as string[]}
              ></FieldErrors>
              <FieldErrors
                errors={getFieldErrors("username", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>

        {initialUser?.id !== undefined ? (
          <></>
        ) : (
          <>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Contraseña</CLabel>
                </CCol>
                <CCol>
                  <CInputGroup>
                    <CInput
                      type={showPassword1 ? "text" : "password"}
                      value={emptyValueOnUndefined(editingUser.password1)}
                      onChange={onPassword1Change}
                      placeholder="Ej: Contraseña"
                      autoComplete="new-password"
                    ></CInput>
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onPassword1ShowClick}
                    >
                      {showPassword1 ? (
                        <i className="fa fa-eye-slash"></i>
                      ) : (
                        <i className="fa fa-eye"></i>
                      )}
                    </CButton>
                  </CInputGroup>
                  <FieldErrors
                    errors={getFieldErrors("password1", errors) as string[]}
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Repita la Contraseña</CLabel>
                </CCol>
                <CCol>
                  <CInputGroup>
                    <CInput
                      type={showPassword2 ? "text" : "password"}
                      value={emptyValueOnUndefined(editingUser.password2)}
                      onChange={onPassword2Change}
                      placeholder="Ej: Repita la Contraseña"
                      autoComplete="new-password"
                    ></CInput>
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onPassword2ShowClick}
                    >
                      {showPassword2 ? (
                        <i className="fa fa-eye-slash"></i>
                      ) : (
                        <i className="fa fa-eye"></i>
                      )}
                    </CButton>
                  </CInputGroup>
                  <FieldErrors
                    errors={getFieldErrors("password2", errors) as string[]}
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>
          </>
        )}

        <CFormGroup>
          <CRow className={"mt-2"}>
            <CCol md={2}>
              <CLabel className={"mt-2"}>Rol:</CLabel>
            </CCol>
            <CCol>
              <CSelect
                type="text"
                value={emptyValueOnUndefined(editingUser.role) || ""}
                onChange={onRoleChange}
              >
                <option value={""}>-----</option>
                {Array.from(ROLES.entries()).map((entry) => {
                  return (
                    <option key={entry[0]} value={entry[0]}>
                      {entry[1]}
                    </option>
                  );
                })}
              </CSelect>
              <FieldErrors
                errors={getFieldErrors("reasonCode", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>

        <CRow className="mb-2">
          <CCol>
            <h5>
              <u>Permisos</u>
            </h5>
          </CCol>
        </CRow>
        <CFormGroup>
          {Array.from(PERMISSION_GROUPS.entries()).map((entry) => (
            <CRow key={entry[0]}>
              <CCol md={2}>
                <CLabel>{entry[0]}</CLabel>
              </CCol>
              <CCol md={10}>
                <CRow>
                  {entry[1].map((value) => (
                    <CCol key={value.value} md={3}>
                      <CInputCheckbox
                        id={`permission_${value.value}`}
                        checked={
                          editingUser.permissions !== undefined &&
                          editingUser.permissions.indexOf(value.value) >= 0
                        }
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          onPermissionsChange(value.value, e.target.checked);
                        }}
                      ></CInputCheckbox>
                      <CLabel htmlFor={`permission_${value.value}`}>
                        {value.label}
                      </CLabel>
                    </CCol>
                  ))}
                </CRow>
              </CCol>
            </CRow>
          ))}
        </CFormGroup>
        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
            <CButton type="submit" color="primary" onClick={onSave}>
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default UserForm;
